import { useState } from 'react';
import { Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Button, Table, TableBody, TableCell, TableContainer, TableRow, CircularProgress, Alert } from '@mui/material';
import { Download } from 'lucide-react';
import { useSubmissions, useMetrics } from '@lingaai/shared-hooks';
import type { Fund } from '@lingaai/types';
import { downloadCSV, downloadExcel, formatMetricValue } from '@lingaai/shared-utils';

interface MetricsDownloadProps {
  companyId: string;
  fund: Fund;
  currentPeriod: string;
}

export function MetricsDownload({ companyId, fund, currentPeriod }: MetricsDownloadProps) {
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);
  const { data: submissions, isLoading } = useSubmissions(companyId);

  // Generate period options (current + last 7 periods)
  const generatePeriodOptions = (): string[] => {
    if (!fund.reporting_frequency || !currentPeriod) return [];

    const periods: string[] = [];
    const [currentQ, currentY] = currentPeriod.split(' ');
    if (!currentQ || !currentY) return [currentPeriod];

    let quarterNum = parseInt(currentQ.replace('Q', ''));
    let year = parseInt(currentY);

    // Generate 8 periods (current + 7 previous)
    for (let i = 0; i < 8; i++) {
      periods.push(`Q${quarterNum} ${year}`);

      quarterNum--;
      if (quarterNum < 1) {
        quarterNum = 4;
        year--;
      }
    }

    return periods;
  };

  const periodOptions = generatePeriodOptions();

  // Find submission for selected period
  const selectedSubmission = submissions?.find((s) => s.period === selectedPeriod);

  // Fetch metrics for selected submission
  const { data: metrics } = useMetrics(selectedSubmission?.id || '');

  const handleDownload = (format: 'csv' | 'excel') => {
    if (!selectedSubmission) {
      alert('No submission selected');
      return;
    }

    if (!metrics || metrics.length === 0) {
      alert('No metrics available to export');
      return;
    }

    // Format metrics for export
    const exportData = metrics.map((metric: any) => ({
      Metric: metric.metric_definitions?.name || 'Unknown',
      Value: formatMetricValue(metric.value),
      Confidence: metric.confidence ? `${(metric.confidence * 100).toFixed(0)}%` : '-',
      Source: metric.source_type || '-',
      'Source Location': metric.source_location || '-',
      Reviewed: metric.reviewed ? 'Yes' : 'No',
    }));

    const filename = `${selectedPeriod.replace(' ', '-')}-metrics`;

    if (format === 'csv') {
      downloadCSV(exportData, filename);
    } else {
      downloadExcel(exportData, filename);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Download Metrics
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Period</InputLabel>
            <Select value={selectedPeriod} label="Period" onChange={(e) => setSelectedPeriod(e.target.value)}>
              {periodOptions.map((period) => (
                <MenuItem key={period} value={period}>
                  {period}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Download size={18} strokeWidth={1} />}
              onClick={() => handleDownload('csv')}
              disabled={!selectedSubmission || selectedSubmission.status !== 'completed'}
            >
              CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download size={18} strokeWidth={1} />}
              onClick={() => handleDownload('excel')}
              disabled={!selectedSubmission || selectedSubmission.status !== 'completed'}
            >
              Excel
            </Button>
          </Box>
        </Box>

        {!selectedSubmission && <Alert severity="info">No submission found for {selectedPeriod}. Upload your report first.</Alert>}

        {selectedSubmission && selectedSubmission.status !== 'completed' && (
          <Alert severity="warning">
            Submission for {selectedPeriod} is still being processed. Status: {selectedSubmission.status}
          </Alert>
        )}

        {selectedSubmission && selectedSubmission.status === 'completed' && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Submission Details
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Period</strong>
                    </TableCell>
                    <TableCell>{selectedSubmission.period}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Submitted</strong>
                    </TableCell>
                    <TableCell>
                      {selectedSubmission.submitted_at
                        ? new Date(selectedSubmission.submitted_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Source</strong>
                    </TableCell>
                    <TableCell>{selectedSubmission.source_type === 'email' ? 'Email' : 'Upload'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell>{selectedSubmission.status.charAt(0).toUpperCase() + selectedSubmission.status.slice(1)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          Note: Download Feature
        </Typography>
        <Typography variant="body2">The download functionality will export all metrics extracted from your submission in your chosen format. This feature is currently under development.</Typography>
      </Alert>
    </Box>
  );
}
